import type { FlowDefinition, FlowStep, FlowSourceBinding } from "@/lib/flow-engine/types";
import { FLOW_SCHEMA_VERSION } from "@/lib/flow-engine/types";

function uid(prefix: string) {
  return `${prefix}_${crypto.randomUUID().replace(/-/g, "").slice(0, 12)}`;
}

function slugify(name: string): string {
  return (
    name
      .toLowerCase()
      .normalize("NFD")
      .replace(/\p{M}/gu, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "bundle"
  );
}

function mkPicker(opts: {
  stepId: string;
  key: string;
  label: string;
  description: string;
  order: number;
  bindId: string;
  required?: boolean;
  selectionMode?: "single" | "multiple";
  qty?: FlowStep["qty"];
}): FlowStep {
  return {
    id: opts.stepId,
    key: opts.key,
    type: "product_picker",
    label: opts.label,
    description: opts.description,
    order: opts.order,
    enabled: true,
    required: opts.required ?? true,
    selectionMode: opts.selectionMode ?? "single",
    qty: opts.qty ?? { mode: "exact", exact: 1, min: 1, max: 1 },
    sourceBindingIds: [opts.bindId],
    layout: "grid",
  };
}

function mkBind(bindId: string, stepId: string, overrides: Partial<FlowSourceBinding> = {}): FlowSourceBinding {
  return {
    id: bindId,
    sourceKey: "catalog.products",
    stepId,
    query: {},
    lazy: true,
    required: true,
    selectionMode: "single",
    minSelections: 1,
    maxSelections: 1,
    ...overrides,
  };
}

function mkCategoryBind(bindId: string, stepId: string, categoryName: string, overrides: Partial<FlowSourceBinding> = {}): FlowSourceBinding {
  return {
    id: bindId,
    sourceKey: "catalog.products",
    stepId,
    query: { categoryName },
    lazy: true,
    required: true,
    selectionMode: "single",
    minSelections: 1,
    maxSelections: 1,
    ...overrides,
  };
}

function mkRadio(opts: {
  stepId: string;
  key: string;
  label: string;
  description: string;
  order: number;
  options: Array<{ value: string; label: string }>;
}): FlowStep {
  return {
    id: opts.stepId,
    key: opts.key,
    type: "radio",
    label: opts.label,
    description: opts.description,
    order: opts.order,
    enabled: true,
    required: true,
    selectionMode: "single",
    qty: { mode: "exact", exact: 1 },
    sourceBindingIds: [],
    field: { options: opts.options },
  };
}

function mkSummary(stepId: string, order: number): FlowStep {
  return {
    id: stepId,
    key: "summary",
    type: "summary",
    label: "Resumen",
    description: "Revisá tu selección antes de agregar al carrito.",
    order,
    enabled: true,
    required: false,
    selectionMode: "none",
    qty: { mode: "none" },
    sourceBindingIds: [],
  };
}

function mkTextarea(opts: {
  stepId: string;
  key: string;
  label: string;
  description: string;
  order: number;
  placeholder?: string;
  required?: boolean;
}): FlowStep {
  return {
    id: opts.stepId,
    key: opts.key,
    type: "textarea",
    label: opts.label,
    description: opts.description,
    order: opts.order,
    enabled: true,
    required: opts.required ?? false,
    selectionMode: "none",
    qty: { mode: "none" },
    sourceBindingIds: [],
    field: { placeholder: opts.placeholder ?? "Escribí tu mensaje…" },
  };
}

const BASE_OUTPUT = {
  outputType: "order_draft" as const,
  pricingMode: "checkout" as const,
  handoff: "cart" as const,
  fixedPrice: null,
  allowDiscounts: true,
};

type TemplateInput = {
  tenantId: string;
  name: string;
  slug?: string;
  description?: string;
  imageUrl?: string;
};

// ─── Plantillas ──────────────────────────────────────────────────────────────

/** Caja armada (pack base): estilo → caja → bebida → snack → resumen. */
export function createBundleDefinition(input: TemplateInput): FlowDefinition {
  const bundleId = uid("flow");
  const stepBoxType = uid("step"); const stepBox = uid("step"); const stepDrink = uid("step");
  const stepSnack = uid("step"); const stepSummary = uid("step");
  const bindBox = uid("bind"); const bindDrink = uid("bind"); const bindSnack = uid("bind");
  const now = new Date().toISOString();

  return {
    schemaVersion: FLOW_SCHEMA_VERSION,
    id: bundleId, tenantId: input.tenantId,
    slug: input.slug?.trim() || slugify(input.name),
    name: input.name.trim(),
    description: input.description ?? "Armá tu caja: elegís el estilo, los productos, la bebida y el snack.",
    presetId: "bundle", lifecycleStatus: "draft", version: 0,
    requiredCapabilities: ["composable_flows"], minPlanTier: "pro",
    graph: {
      entryStepId: stepBoxType,
      steps: [
        mkRadio({ stepId: stepBoxType, key: "box_type", order: 0, label: "Estilo de caja", description: "Elegí el estilo.", options: [{ value: "standard", label: "Estándar" }, { value: "premium", label: "Premium" }] }),
        mkPicker({ stepId: stepBox, key: "box", label: "Elegí la caja", description: "El producto principal del pack.", order: 1, bindId: bindBox }),
        mkPicker({ stepId: stepDrink, key: "drink", label: "Sumá una bebida", description: "Acompañá con tu bebida favorita.", order: 2, bindId: bindDrink }),
        mkPicker({ stepId: stepSnack, key: "snack", label: "Sumá un snack", description: "Algo rico para completar el pack.", order: 3, bindId: bindSnack }),
        mkSummary(stepSummary, 4),
      ],
      transitions: [],
    },
    sourceBindings: [
      mkCategoryBind(bindBox, stepBox, "Cajas"),
      mkCategoryBind(bindDrink, stepDrink, "Bebidas"),
      mkCategoryBind(bindSnack, stepSnack, "Snacks"),
    ],
    rules: [{
      id: uid("rule"), key: "premium_discount", name: "Descuento Premium",
      enabled: true, priority: 10,
      when: { type: "variable", key: "box_type", op: "eq", value: "premium" },
      effects: [{ type: "discount_percent", percent: 10, priority: 5 }],
    }],
    outputSpec: BASE_OUTPUT,
    createdAt: now, updatedAt: now,
    imageUrl: input.imageUrl,
    metadata: {
      templateId: "default",
      accentColor: "#16a34a",
      placeholderImageUrl: "https://images.unsplash.com/photo-1606791422814-b32c705e3e2f?w=600&q=80",
    },
  };
}

/** Combo de comida: plato + acompañamiento + bebida + postre opcional. */
export function createMealComboDefinition(input: TemplateInput): FlowDefinition {
  const flowId = uid("flow");
  const s1 = uid("step"); const s2 = uid("step"); const s3 = uid("step"); const s4 = uid("step"); const s5 = uid("step");
  const b1 = uid("bind"); const b2 = uid("bind"); const b3 = uid("bind"); const b4 = uid("bind");
  const now = new Date().toISOString();

  return {
    schemaVersion: FLOW_SCHEMA_VERSION,
    id: flowId, tenantId: input.tenantId,
    slug: input.slug?.trim() || slugify(input.name),
    name: input.name.trim(),
    description: input.description ?? "Armá tu combo: plato, acompañamiento, bebida y postre opcional.",
    presetId: "bundle", lifecycleStatus: "draft", version: 0,
    requiredCapabilities: ["composable_flows"], minPlanTier: "pro",
    graph: {
      entryStepId: s1,
      steps: [
        mkPicker({ stepId: s1, key: "main", label: "Plato principal", description: "El plato central del combo.", order: 0, bindId: b1 }),
        mkPicker({ stepId: s2, key: "side", label: "Acompañamiento", description: "Ensalada, papas, arroz o lo que prefieras.", order: 1, bindId: b2 }),
        mkPicker({ stepId: s3, key: "drink", label: "Bebida", description: "Refresco, agua, jugo o cerveza.", order: 2, bindId: b3 }),
        mkPicker({ stepId: s4, key: "dessert", label: "Postre (opcional)", description: "Podés sumar un postre al combo.", order: 3, bindId: b4, required: false }),
        mkSummary(s5, 4),
      ],
      transitions: [],
    },
    sourceBindings: [
      mkCategoryBind(b1, s1, "Platos"),
      mkCategoryBind(b2, s2, "Acompañamientos"),
      mkCategoryBind(b3, s3, "Bebidas"),
      mkCategoryBind(b4, s4, "Postres", { required: false, minSelections: 0 }),
    ],
    rules: [],
    outputSpec: BASE_OUTPUT,
    createdAt: now, updatedAt: now,
    imageUrl: input.imageUrl,
    metadata: {
      templateId: "meal",
      accentColor: "#ea580c",
      placeholderImageUrl: "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=600&q=80",
    },
  };
}

/** Caja de regalos con mensaje personalizado. */
export function createGiftBoxDefinition(input: TemplateInput): FlowDefinition {
  const flowId = uid("flow");
  const sSize = uid("step"); const s1 = uid("step"); const s2 = uid("step"); const s3 = uid("step"); const sMsg = uid("step"); const sSummary = uid("step");
  const b1 = uid("bind"); const b2 = uid("bind"); const b3 = uid("bind");
  const now = new Date().toISOString();

  return {
    schemaVersion: FLOW_SCHEMA_VERSION,
    id: flowId, tenantId: input.tenantId,
    slug: input.slug?.trim() || slugify(input.name),
    name: input.name.trim(),
    description: input.description ?? "Armá tu caja de regalo: elegís los productos y escribís un mensaje especial.",
    presetId: "bundle", lifecycleStatus: "draft", version: 0,
    requiredCapabilities: ["composable_flows"], minPlanTier: "pro",
    graph: {
      entryStepId: sSize,
      steps: [
        mkRadio({ stepId: sSize, key: "size", order: 0, label: "Tamaño de la caja", description: "¿Para cuántas personas?", options: [{ value: "small", label: "Pequeña (1-2 pers.)" }, { value: "medium", label: "Mediana (3-4 pers.)" }, { value: "large", label: "Grande (5+ pers.)" }] }),
        mkPicker({ stepId: s1, key: "item_1", label: "Producto principal", description: "El artículo estrella de la caja.", order: 1, bindId: b1 }),
        mkPicker({ stepId: s2, key: "item_2", label: "Complemento", description: "Sumá un producto complementario.", order: 2, bindId: b2 }),
        mkPicker({ stepId: s3, key: "item_3", label: "Extra (opcional)", description: "Un tercer producto para completar el regalo.", order: 3, bindId: b3, required: false }),
        mkTextarea({ stepId: sMsg, key: "message", order: 4, label: "Mensaje para el destinatario", description: "Lo incluimos en una tarjeta dentro de la caja.", placeholder: "Ej. ¡Feliz cumpleaños! Con mucho cariño…", required: false }),
        mkSummary(sSummary, 5),
      ],
      transitions: [],
    },
    sourceBindings: [
      mkBind(b1, s1),
      mkBind(b2, s2),
      mkBind(b3, s3, { required: false, minSelections: 0 }),
    ],
    rules: [{
      id: uid("rule"), key: "large_discount", name: "Descuento caja grande",
      enabled: true, priority: 10,
      when: { type: "variable", key: "size", op: "eq", value: "large" },
      effects: [{ type: "discount_percent", percent: 15, priority: 5 }],
    }],
    outputSpec: BASE_OUTPUT,
    createdAt: now, updatedAt: now,
    imageUrl: input.imageUrl,
    metadata: {
      templateId: "gift_box",
      accentColor: "#9333ea",
      placeholderImageUrl: "https://images.unsplash.com/photo-1549465220-1a8b9238cd48?w=600&q=80",
    },
  };
}

/** Caja de vinos: varietal + maridaje + madera opcional + tarjeta sommelier. */
export function createWineBoxDefinition(input: TemplateInput): FlowDefinition {
  const flowId = uid("flow");
  const sStyle = uid("step"); const sWine1 = uid("step"); const sWine2 = uid("step");
  const sPairing = uid("step"); const sWood = uid("step"); const sMsg = uid("step"); const sSummary = uid("step");
  const bWine1 = uid("bind"); const bWine2 = uid("bind"); const bPairing = uid("bind"); const bWood = uid("bind");
  const now = new Date().toISOString();

  return {
    schemaVersion: FLOW_SCHEMA_VERSION,
    id: flowId, tenantId: input.tenantId,
    slug: input.slug?.trim() || slugify(input.name),
    name: input.name.trim(),
    description: input.description ?? "Armá tu caja de vinos: elegís los varietales, el maridaje y lo que acompaña.",
    presetId: "bundle", lifecycleStatus: "draft", version: 0,
    requiredCapabilities: ["composable_flows"], minPlanTier: "pro",
    graph: {
      entryStepId: sStyle,
      steps: [
        mkRadio({ stepId: sStyle, key: "box_style", order: 0, label: "Tipo de caja", description: "¿Qué tipo de vinos buscás?", options: [{ value: "tinto", label: "Tintos" }, { value: "blanco", label: "Blancos" }, { value: "mixta", label: "Mixta" }, { value: "espumante", label: "Espumantes" }] }),
        mkPicker({ stepId: sWine1, key: "wine_1", label: "Primer vino", description: "El primero de tu selección.", order: 1, bindId: bWine1 }),
        mkPicker({ stepId: sWine2, key: "wine_2", label: "Segundo vino", description: "El segundo de tu selección.", order: 2, bindId: bWine2 }),
        mkPicker({ stepId: sPairing, key: "pairing", label: "Maridaje", description: "Quesos, fiambres o tabla para acompañar.", order: 3, bindId: bPairing }),
        mkPicker({ stepId: sWood, key: "wood", label: "Madera (opcional)", description: "Tabla de madera, sacacorchos o accesorio.", order: 4, bindId: bWood, required: false }),
        mkTextarea({ stepId: sMsg, key: "message", order: 5, label: "Nota del sommelier / dedicatoria", description: "Texto personalizado para incluir en la caja.", placeholder: "Ej. Selección especial para…", required: false }),
        mkSummary(sSummary, 6),
      ],
      transitions: [],
    },
    sourceBindings: [
      mkCategoryBind(bWine1, sWine1, "Vinos"),
      mkCategoryBind(bWine2, sWine2, "Vinos"),
      mkCategoryBind(bPairing, sPairing, "Maridaje"),
      mkCategoryBind(bWood, sWood, "Accesorios", { required: false, minSelections: 0 }),
    ],
    rules: [{
      id: uid("rule"), key: "espumante_premium", name: "Recargo espumantes",
      enabled: true, priority: 10,
      when: { type: "variable", key: "box_style", op: "eq", value: "espumante" },
      effects: [{ type: "surcharge_percent", percent: 5, priority: 5 }],
    }],
    outputSpec: BASE_OUTPUT,
    createdAt: now, updatedAt: now,
    imageUrl: input.imageUrl,
    metadata: {
      templateId: "wine_box",
      accentColor: "#7c3aed",
      placeholderImageUrl: "https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=600&q=80",
    },
  };
}

/** Kit de producto: principal + accesorios. */
export function createKitDefinition(input: TemplateInput): FlowDefinition {
  const flowId = uid("flow");
  const s1 = uid("step"); const s2 = uid("step"); const s3 = uid("step"); const s4 = uid("step");
  const b1 = uid("bind"); const b2 = uid("bind"); const b3 = uid("bind");
  const now = new Date().toISOString();

  return {
    schemaVersion: FLOW_SCHEMA_VERSION,
    id: flowId, tenantId: input.tenantId,
    slug: input.slug?.trim() || slugify(input.name),
    name: input.name.trim(),
    description: input.description ?? "Kit completo: un producto principal con los accesorios que necesitás.",
    presetId: "bundle", lifecycleStatus: "draft", version: 0,
    requiredCapabilities: ["composable_flows"], minPlanTier: "pro",
    graph: {
      entryStepId: s1,
      steps: [
        mkPicker({ stepId: s1, key: "main", label: "Producto principal", description: "El artículo central del kit.", order: 0, bindId: b1 }),
        mkPicker({ stepId: s2, key: "acc_1", label: "Accesorio", description: "Complemento 1.", order: 1, bindId: b2 }),
        mkPicker({ stepId: s3, key: "acc_2", label: "Segundo accesorio (opcional)", description: "Complemento adicional.", order: 2, bindId: b3, required: false }),
        mkSummary(s4, 3),
      ],
      transitions: [],
    },
    sourceBindings: [
      mkBind(b1, s1),
      mkBind(b2, s2),
      mkBind(b3, s3, { required: false, minSelections: 0 }),
    ],
    rules: [],
    outputSpec: BASE_OUTPUT,
    createdAt: now, updatedAt: now,
    imageUrl: input.imageUrl,
    metadata: {
      templateId: "kit",
      accentColor: "#0284c7",
      placeholderImageUrl: "https://images.unsplash.com/photo-1591664460942-6d6e82df9fec?w=600&q=80",
    },
  };
}

/** Mix & Match: el cliente elige N productos del catálogo libremente. */
export function createMixMatchDefinition(input: TemplateInput): FlowDefinition {
  const flowId = uid("flow");
  const s1 = uid("step"); const s2 = uid("step");
  const b1 = uid("bind");
  const now = new Date().toISOString();

  return {
    schemaVersion: FLOW_SCHEMA_VERSION,
    id: flowId, tenantId: input.tenantId,
    slug: input.slug?.trim() || slugify(input.name),
    name: input.name.trim(),
    description: input.description ?? "El cliente elige cualquier combinación de productos (hasta el máximo que definas).",
    presetId: "bundle", lifecycleStatus: "draft", version: 0,
    requiredCapabilities: ["composable_flows"], minPlanTier: "pro",
    graph: {
      entryStepId: s1,
      steps: [
        {
          id: s1, key: "selection", type: "product_picker",
          label: "Elegí tus productos",
          description: "Podés elegir hasta 6 productos de nuestra selección.",
          order: 0, enabled: true, required: true,
          selectionMode: "multiple",
          qty: { mode: "min_max", min: 1, max: 6 },
          sourceBindingIds: [b1], layout: "grid",
        },
        mkSummary(s2, 1),
      ],
      transitions: [],
    },
    sourceBindings: [{
      id: b1, sourceKey: "catalog.products", stepId: s1,
      query: {}, lazy: true, required: true,
      selectionMode: "multiple", minSelections: 1, maxSelections: 6,
    }],
    rules: [{
      id: uid("rule"), key: "bulk_discount", name: "Descuento por volumen (3+)",
      enabled: true, priority: 10,
      when: { type: "selection", stepKey: "selection", op: "count_gte", value: 3 },
      effects: [{ type: "discount_percent", percent: 10, priority: 5 }],
    }],
    outputSpec: BASE_OUTPUT,
    createdAt: now, updatedAt: now,
    imageUrl: input.imageUrl,
    metadata: {
      templateId: "mix_match",
      accentColor: "#db2777",
      placeholderImageUrl: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80",
    },
  };
}

/** Combo fast food: tamaño → hamburguesa/plato → papas/acompañamiento → bebida → extra opcional. */
export function createFastFoodComboDefinition(input: TemplateInput): FlowDefinition {
  const flowId = uid("flow");
  const sSize = uid("step"); const sMain = uid("step"); const sSide = uid("step");
  const sDrink = uid("step"); const sExtra = uid("step"); const sSummary = uid("step");
  const bMain = uid("bind"); const bSide = uid("bind"); const bDrink = uid("bind"); const bExtra = uid("bind");
  const now = new Date().toISOString();

  return {
    schemaVersion: FLOW_SCHEMA_VERSION,
    id: flowId, tenantId: input.tenantId,
    slug: input.slug?.trim() || slugify(input.name),
    name: input.name.trim(),
    description: input.description ?? "Combo completo: elegís el tamaño, el plato, las papas y la bebida.",
    presetId: "bundle", lifecycleStatus: "draft", version: 0,
    requiredCapabilities: ["composable_flows"], minPlanTier: "pro",
    graph: {
      entryStepId: sSize,
      steps: [
        mkRadio({ stepId: sSize, key: "size", order: 0, label: "Tamaño del combo", description: "¿Qué tamaño querés?", options: [{ value: "simple", label: "Simple" }, { value: "doble", label: "Doble" }, { value: "triple", label: "Triple" }] }),
        mkPicker({ stepId: sMain, key: "main", label: "Elegí tu burger / plato", description: "El plato central del combo.", order: 1, bindId: bMain }),
        mkPicker({ stepId: sSide, key: "side", label: "Papas / acompañamiento", description: "Papas fritas, wedges, ensalada…", order: 2, bindId: bSide }),
        mkPicker({ stepId: sDrink, key: "drink", label: "Bebida", description: "Refresco, agua o jugo.", order: 3, bindId: bDrink }),
        mkPicker({ stepId: sExtra, key: "extra", label: "Extra (opcional)", description: "Salsa, dip o algo más.", order: 4, bindId: bExtra, required: false }),
        mkSummary(sSummary, 5),
      ],
      transitions: [],
    },
    sourceBindings: [
      mkCategoryBind(bMain, sMain, "Burgers"),
      mkCategoryBind(bSide, sSide, "Acompañamientos"),
      mkCategoryBind(bDrink, sDrink, "Bebidas"),
      mkCategoryBind(bExtra, sExtra, "Extras", { required: false, minSelections: 0 }),
    ],
    rules: [{
      id: uid("rule"), key: "doble_surcharge", name: "Recargo doble",
      enabled: true, priority: 10,
      when: { type: "variable", key: "size", op: "eq", value: "doble" },
      effects: [{ type: "surcharge_fixed", amount: 500, priority: 5 }],
    }, {
      id: uid("rule"), key: "triple_surcharge", name: "Recargo triple",
      enabled: true, priority: 9,
      when: { type: "variable", key: "size", op: "eq", value: "triple" },
      effects: [{ type: "surcharge_fixed", amount: 900, priority: 5 }],
    }],
    outputSpec: BASE_OUTPUT,
    createdAt: now, updatedAt: now,
    imageUrl: input.imageUrl,
    metadata: {
      templateId: "fast_food",
      accentColor: "#dc2626",
      placeholderImageUrl: "https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=600&q=80",
    },
  };
}

/** Bundle en blanco — el merchant define todo desde cero. */
export function createBlankDefinition(input: TemplateInput): FlowDefinition {
  const flowId = uid("flow");
  const s1 = uid("step"); const s2 = uid("step");
  const b1 = uid("bind");
  const now = new Date().toISOString();

  return {
    schemaVersion: FLOW_SCHEMA_VERSION,
    id: flowId, tenantId: input.tenantId,
    slug: input.slug?.trim() || slugify(input.name),
    name: input.name.trim(),
    description: input.description ?? "",
    presetId: "bundle", lifecycleStatus: "draft", version: 0,
    requiredCapabilities: ["composable_flows"], minPlanTier: "pro",
    graph: {
      entryStepId: s1,
      steps: [
        mkPicker({ stepId: s1, key: "item_1", label: "Elegí un producto", description: "Configurá de dónde salen los productos en la sección Fuentes.", order: 0, bindId: b1 }),
        mkSummary(s2, 1),
      ],
      transitions: [],
    },
    sourceBindings: [mkBind(b1, s1)],
    rules: [],
    outputSpec: BASE_OUTPUT,
    createdAt: now, updatedAt: now,
    imageUrl: input.imageUrl,
    metadata: {
      templateId: "blank",
      accentColor: "#475569",
      placeholderImageUrl: null,
    },
  };
}

// ─── Catálogo de templates para la UI ────────────────────────────────────────

export type BundleTemplateId = "default" | "meal" | "gift_box" | "wine_box" | "kit" | "mix_match" | "fast_food" | "blank";

export type BundleTemplate = {
  id: BundleTemplateId;
  emoji: string;
  name: string;
  description: string;
  example: string;
  accentColor: string;
  create: (input: TemplateInput) => FlowDefinition;
};

export const BUNDLE_TEMPLATES: BundleTemplate[] = [
  {
    id: "default",
    emoji: "📦",
    name: "Pack armado",
    description: "El cliente elige el estilo, el producto principal, la bebida y el snack.",
    example: "Caja premium + bebida + snack",
    accentColor: "#16a34a",
    create: createBundleDefinition,
  },
  {
    id: "meal",
    emoji: "🍽️",
    name: "Combo de comida",
    description: "Plato principal + acompañamiento + bebida + postre opcional.",
    example: "Hamburguesa + papas + Coca + helado",
    accentColor: "#ea580c",
    create: createMealComboDefinition,
  },
  {
    id: "gift_box",
    emoji: "🎁",
    name: "Caja de regalo",
    description: "El cliente elige los productos y escribe un mensaje para el destinatario.",
    example: "Vino + tabla + quesos + tarjeta",
    accentColor: "#9333ea",
    create: createGiftBoxDefinition,
  },
  {
    id: "wine_box",
    emoji: "🍷",
    name: "Caja de vinos",
    description: "Varietales + maridaje + accesorio opcional + nota del sommelier.",
    example: "Malbec + Chardonnay + tabla de quesos",
    accentColor: "#7c3aed",
    create: createWineBoxDefinition,
  },
  {
    id: "kit",
    emoji: "🧰",
    name: "Kit de producto",
    description: "Un producto principal con accesorios o complementos.",
    example: "Cafetera + café + filtros",
    accentColor: "#0284c7",
    create: createKitDefinition,
  },
  {
    id: "mix_match",
    emoji: "🎨",
    name: "Mix & Match",
    description: "El cliente elige N productos libremente. Descuento automático por volumen.",
    example: "Elegí 3 alfajores de cualquier sabor",
    accentColor: "#db2777",
    create: createMixMatchDefinition,
  },
  {
    id: "fast_food",
    emoji: "🍔",
    name: "Combo fast food",
    description: "Tamaño → plato / burger → papas → bebida → extra opcional.",
    example: "Doble burger + papas + Coca",
    accentColor: "#dc2626",
    create: createFastFoodComboDefinition,
  },
  {
    id: "blank",
    emoji: "✏️",
    name: "En blanco",
    description: "Empieza con un solo paso vacío. Ideal si sabés exactamente lo que querés armar.",
    example: "Personalizado desde cero",
    accentColor: "#475569",
    create: createBlankDefinition,
  },
];
